/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { graphql, useStaticQuery } from 'gatsby';
import React, { ReactNode, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { createSelector } from 'reselect';

import hackZeroSlashBoldURL from '../../../static/fonts/hack-zeroslash/Hack-ZeroSlash-Bold.woff';
import hackZeroSlashItalicURL from '../../../static/fonts/hack-zeroslash/Hack-ZeroSlash-Italic.woff';
import hackZeroSlashRegularURL from '../../../static/fonts/hack-zeroslash/Hack-ZeroSlash-Regular.woff';
import latoBoldURL from '../../../static/fonts/lato/Lato-Bold.woff';
import latoLightURL from '../../../static/fonts/lato/Lato-Light.woff';
import latoRegularURL from '../../../static/fonts/lato/Lato-Regular.woff';

import robotoBoldURL from '../../../static/fonts/roboto-mono/RobotoMono-Bold.woff';
import robotoItalicURL from '../../../static/fonts/roboto-mono/RobotoMono-Italic.woff';
import robotoRegularURL from '../../../static/fonts/roboto-mono/RobotoMono-Regular.woff';

import poppinsBoldURL from '../../../static/fonts/poppins/Poppins-Bold.ttf';
import poppinsLightURL from '../../../static/fonts/poppins/Poppins-Light.ttf';
import poppinsMediumURL from '../../../static/fonts/poppins/Poppins-Medium.ttf';
import poppinsRegularURL from '../../../static/fonts/poppins/Poppins-Regular.ttf';

import consolasRegulas from '../../../static/fonts/Consolas/CONSOLA.ttf';

import montserratBlackURL from '../../../static/fonts/montserrat/Montserrat-Black.ttf';
import montserratRegularURL from '../../../static/fonts/montserrat/Montserrat-Regular.ttf';
import montserratSemiBoldURL from '../../../static/fonts/montserrat/Montserrat-SemiBold.ttf';

import futuraBookRegularURL from '../../../static/fonts/futura-book/FuturaBookRegular.otf';

import { isBrowser } from '../../../utils';
import {
  fetchUser,
  onlineStatusChange,
  serverStatusChange,
  updateAllChallengesInfo
} from '../../redux/actions';
import {
  isOnlineSelector,
  isServerOnlineSelector,
  isSignedInSelector,
  showCodeAllySelector,
  themeSelector,
  userFetchStateSelector,
  userSelector
} from '../../redux/selectors';

import {
  AllChallengeNode,
  CertificateNode,
  User,
  UserFetchState
} from '../../redux/prop-types';
import Flash from '../Flash';
import { flashMessageSelector, removeFlashMessage } from '../Flash/redux';
import Footer from '../Footer';
import Header from '../Header';
import OfflineWarning from '../OfflineWarning';
import { Loader } from '../helpers';
import ModalCtaRegistration from '../modals/ModalCtaRegistration';
import SignoutModal from '../modals/signout-modal';

// preload common fonts
import './fonts.css';
import './global.css';
import './rtl-layout.css';
import './variables.css';

const mapStateToProps = createSelector(
  isSignedInSelector,
  flashMessageSelector,
  isOnlineSelector,
  isServerOnlineSelector,
  userFetchStateSelector,
  showCodeAllySelector,
  userSelector,
  themeSelector,
  (
    isSignedIn,
    flashMessage,
    isOnline: boolean,
    isServerOnline: boolean,
    fetchState: UserFetchState,
    showCodeAlly: boolean,
    user: User
  ) => ({
    isSignedIn,
    flashMessage,
    hasMessage: !!flashMessage.message,
    isOnline,
    isServerOnline,
    fetchState,
    theme: user.theme,
    showCodeAlly,
    user
  })
);

type StateProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      fetchUser,
      removeFlashMessage,
      onlineStatusChange,
      serverStatusChange,
      updateAllChallengesInfo
    },
    dispatch
  );

type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface DefaultLayoutProps extends StateProps, DispatchProps {
  readonly children: ReactNode;
  readonly showFooter?: boolean;
  readonly isChallenge?: boolean;
}

function DefaultLayout({
  children,
  hasMessage,
  fetchState,
  flashMessage,
  isOnline,
  isServerOnline,
  isSignedIn,
  removeFlashMessage,
  showFooter = true,
  user,
  isChallenge,
  fetchUser,
  updateAllChallengesInfo
}: DefaultLayoutProps): JSX.Element {
  const { t } = useTranslation();
  const { challengeEdges, certificateNodes } = useGetAllBlockIds();

  useEffect(() => {
    // componentDidMount
    updateAllChallengesInfo({ challengeEdges, certificateNodes });
    if (!isSignedIn) {
      fetchUser();
    }
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      // componentWillUnmount.
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateOnlineStatus = () => {
    const isOnline =
      isBrowser() && 'navigator' in window ? window.navigator.onLine : null;
    return typeof isOnline === 'boolean' ? onlineStatusChange(isOnline) : null;
  };

  if (fetchState.pending) {
    return <Loader fullScreen={true} messageDelay={5000} />;
  } else {
    return (
      <div className='page-wrapper'>
        <Helmet
          meta={[
            {
              name: 'description',
              content: t('metaTags:description') ?? ''
            },
            { name: 'keywords', content: t('metaTags:keywords') ?? '' }
          ]}
        >
          <link
            as='font'
            crossOrigin='anonymous'
            href={latoRegularURL}
            rel='preload'
            type='font/woff'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={latoLightURL}
            rel='preload'
            type='font/woff'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={latoBoldURL}
            rel='preload'
            type='font/woff'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={hackZeroSlashRegularURL}
            rel='preload'
            type='font/woff'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={hackZeroSlashBoldURL}
            rel='preload'
            type='font/woff'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={hackZeroSlashItalicURL}
            rel='preload'
            type='font/woff'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={robotoRegularURL}
            rel='preload'
            type='font/woff'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={robotoBoldURL}
            rel='preload'
            type='font/woff'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={robotoItalicURL}
            rel='preload'
            type='font/woff'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={poppinsLightURL}
            rel='preload'
            type='font/ttf'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={poppinsRegularURL}
            rel='preload'
            type='font/ttf'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={poppinsMediumURL}
            rel='preload'
            type='font/ttf'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={poppinsBoldURL}
            rel='preload'
            type='font/ttf'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={consolasRegulas}
            rel='preload'
            type='font/ttf'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={futuraBookRegularURL}
            rel='preload'
            type='font/otf'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={montserratRegularURL}
            rel='preload'
            type='font/ttf'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={montserratSemiBoldURL}
            rel='preload'
            type='font/ttf'
          />
          <link
            as='font'
            crossOrigin='anonymous'
            href={montserratBlackURL}
            rel='preload'
            type='font/ttf'
          />
        </Helmet>
        <div
          className={`default-layout ${
            isChallenge ? 'container-exercice' : ''
          }`}
        >
          <Header fetchState={fetchState} user={user} />
          <OfflineWarning
            isOnline={isOnline}
            isServerOnline={isServerOnline}
            isSignedIn={isSignedIn}
          />
          {hasMessage && flashMessage ? (
            <Flash
              flashMessage={flashMessage}
              removeFlashMessage={removeFlashMessage}
            />
          ) : null}
          <SignoutModal />
          <ModalCtaRegistration />
          <div id='content-start' tabIndex={-1}>
            {(fetchState.complete || fetchState.errored) && children}
          </div>
        </div>
        {showFooter && <Footer />}
      </div>
    );
  }
}

// TODO: get challenge nodes directly rather than wrapped in edges
const useGetAllBlockIds = () => {
  const {
    allChallengeNode: { edges: challengeEdges },
    allCertificateNode: { nodes: certificateNodes }
  }: {
    allChallengeNode: AllChallengeNode;
    allCertificateNode: { nodes: CertificateNode[] };
  } = useStaticQuery(graphql`
    query getBlockNode {
      allChallengeNode(
        sort: {
          fields: [
            challenge___superOrder
            challenge___order
            challenge___challengeOrder
          ]
        }
      ) {
        edges {
          node {
            challenge {
              block
              id
            }
          }
        }
      }
      allCertificateNode {
        nodes {
          challenge {
            certification
            tests {
              id
            }
          }
        }
      }
    }
  `);

  return { challengeEdges, certificateNodes };
};

DefaultLayout.displayName = 'DefaultLayout';

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(DefaultLayout));
